import {
  ActionEventTitle,
  CalculatedDataEntry,
  CalculatedSessionDetails,
  ConsentEvent,
  OnsiteCampaignEvent,
  OnsiteCampaignStatistics,
  PersonDetails,
  SessionAction,
  SessionActionType,
  SessionDetails,
  Visit,
  VisitGoal,
} from './flyout-models';
import { LanguageService } from '../core/language.service';
import { WidgetSimpleInfo } from '../campaign/widgets.model';
import TimeUtils from 'app/shared/utils/time-utils';

export default class FlyoutHelpers {
  public static parseOnsiteCampaignActivity(
    widgetsToParse: Array<OnsiteCampaignEvent>,
    widgetList: Array<WidgetSimpleInfo>,
    languageService,
  ): Array<OnsiteCampaignEvent> {
    const ocEvents = [];
    if (!widgetsToParse) {
      return ocEvents;
    }
    const widgets = widgetList ?? [];

    const array = widgetsToParse.filter((x) => x.type !== 'Dismissal').reverse();
    for (const event of array) {
      if (!ocEvents.find((x) => x.id === event.id && x.type === event.type)) {
        event.count = array.filter((x) => x.id === event.id && x.type === event.type).length;
        ocEvents.push(event);
      }
    }
    for (const event of ocEvents) {
      if (widgets.find((x) => x.id.toString() === event.id && x.campaignName !== '')) {
        event.campaignName = widgets.find((x) => x.id.toString() === event.id)?.campaignName;
      } else if (widgets.find((x) => x.id.toString() === event.id && x.campaignName === '')) {
        event.campaignName = `Widget ${event.id}`;
      } else if (!widgets.find((x) => x.id.toString() === event.id)) {
        event.campaignName = languageService.translateInstantly('visits.goalEvents.widgetRemoved', {
          widgetId: event.id,
        });
        event.deletedWidget = true;
      }
    }
    return ocEvents;
  }

  public static parseCalculatedPropertiesOnSession(
    sessionDetails: SessionDetails,
    widgetIdToCampaignIdMap: { number: number },
    campaignIdToCampaignNameMap: { number: string },
    languageService: LanguageService,
  ) {
    if (!sessionDetails) {
      return sessionDetails;
    }

    const calculated = {} as CalculatedSessionDetails;
    if (sessionDetails.actionsList) {
      sessionDetails.actionsList = sessionDetails.actionsList.filter(
        (action) =>
          action.type !== SessionActionType.Goal ||
          (action.title.toLowerCase().indexOf('visitedpage-') !== 0 &&
            action.title.toLowerCase().indexOf('oc_submit_') !== 0),
      );
      calculated.goals = FlyoutHelpers.extractActionTypeFromActionList(
        sessionDetails.actionsList,
        SessionActionType.Goal,
      );
      calculated.goals = FlyoutHelpers.populateGoalsWithCorrectNames(
        calculated.goals,
        widgetIdToCampaignIdMap,
        campaignIdToCampaignNameMap,
        languageService,
      );

      calculated.widgetOpens = FlyoutHelpers.extractActionTypeFromActionList(
        sessionDetails.actionsList,
        SessionActionType.WidgetOpen,
      );
      calculated.widgetConversions = FlyoutHelpers.extractActionTypeFromActionList(
        sessionDetails.actionsList,
        SessionActionType.WidgetConversion,
      );
      calculated.widgetClickthroughs = FlyoutHelpers.extractActionTypeFromActionList(
        sessionDetails.actionsList,
        SessionActionType.WidgetClickthrough,
      );
      calculated.widgetDismissals = FlyoutHelpers.extractActionTypeFromActionList(
        sessionDetails.actionsList,
        SessionActionType.WidgetDismissal,
      );
      calculated.scrolls = FlyoutHelpers.extractActionTypeFromActionList(
        sessionDetails.actionsList,
        SessionActionType.Scroll,
      );
      FlyoutHelpers.mergeOnsiteCampaignStats(calculated, widgetIdToCampaignIdMap, campaignIdToCampaignNameMap);
      calculated.clicks = FlyoutHelpers.extractActionTypeFromActionList(
        sessionDetails.actionsList,
        SessionActionType.Click,
      );
      calculated.pageviews = FlyoutHelpers.extractActionTypeFromActionList(
        sessionDetails.actionsList,
        SessionActionType.Pageview,
      );

      // Parse action list
      let previousPageviewTime,
        previousPageviewIndex = undefined,
        firstPageviewIndex = -1;
      for (let i = 0; i < sessionDetails.actionsList.length; i++) {
        // Convert timestamp to text format
        const timestamp = TimeUtils.getMoment(sessionDetails.actionsList[i].time);
        sessionDetails.actionsList[i].formattedTime = timestamp.format('HH:mm:ss');
        // Calculate duration
        if (
          sessionDetails.actionsList[i].type === SessionActionType.Click ||
          sessionDetails.actionsList[i].type === SessionActionType.Pageview ||
          sessionDetails.actionsList[i].type === SessionActionType.Scroll
        ) {
          if (previousPageviewTime) {
            const timeOnPreviousPageview = timestamp.diff(previousPageviewTime, 'seconds');
            sessionDetails.actionsList[previousPageviewIndex].duration = timeOnPreviousPageview;
          }
          previousPageviewTime = sessionDetails.actionsList[i].time;
          previousPageviewIndex = i;
          if (firstPageviewIndex === -1) {
            firstPageviewIndex = i;
          }
        }
      }

      // After the loop calculate duration on last pageview
      if (previousPageviewIndex) {
        if (previousPageviewIndex !== firstPageviewIndex) {
          sessionDetails.actionsList[previousPageviewIndex].duration =
            sessionDetails.timeTotal -
            TimeUtils.getDateDiff(
              sessionDetails.actionsList[previousPageviewIndex].time,
              sessionDetails.actionsList[firstPageviewIndex].time,
              'seconds',
            );
        } else {
          sessionDetails.actionsList[previousPageviewIndex].duration = sessionDetails.timeTotal;
        }
      }
      // Last one, to change names of actions
      sessionDetails.actionsList = FlyoutHelpers.populateActionListWithCorrectNames(
        sessionDetails.actionsList,
        widgetIdToCampaignIdMap,
        campaignIdToCampaignNameMap,
        languageService,
      );
    }
    if (sessionDetails.goals) {
      sessionDetails.goals = this.filterGoalsToBeShown(sessionDetails.goals);
    }
    if (sessionDetails.consents) {
      sessionDetails.consents.forEach((consent: ConsentEvent) => {
        const timestamp = TimeUtils.getMoment(consent.time);
        consent.timestamp = timestamp.valueOf() / 1000;
        consent.formattedTimestamp = timestamp.format('YYYY-MM-DD HH:mm:ss');
        consent.formattedVersionDate = TimeUtils.getFormattedDate(consent.versionDate);
      });
    }
    sessionDetails.calculatedDetails = calculated;
    return sessionDetails;
  }

  private static populateGoalsWithCorrectNames(
    goals: Array<CalculatedDataEntry>,
    widgetIdToCampaignIdMap: { number: number },
    campaignIdToCampaignNameMap: { number: string },
    languageService: LanguageService,
  ): Array<CalculatedDataEntry> {
    if (!goals) {
      return goals;
    }
    for (let i = 0; i < goals.length; i++) {
      goals[i] = FlyoutHelpers.populateGoalWithConvertedName(
        goals[i],
        widgetIdToCampaignIdMap,
        campaignIdToCampaignNameMap,
        languageService,
      );
    }
    return goals;
  }

  private static populateGoalWithConvertedName(
    goal: CalculatedDataEntry,
    widgetIdToCampaignIdMap: { number: number },
    campaignIdToCampaignNameMap: { number: string },
    languageService: LanguageService,
  ): CalculatedDataEntry {
    const goalNameToCheck = goal.name.toLowerCase();
    if (goalNameToCheck.indexOf('oc_') === 0) {
      let widgetId = undefined,
        isClickthrough = false,
        isConversion = false,
        isDiscount = false,
        isAction = false;
      if (goalNameToCheck.indexOf('oc_submit_') !== -1) {
        isConversion = true;
        widgetId = Number(goalNameToCheck.substring(10, goalNameToCheck.length));
      } else if (goalNameToCheck.indexOf('oc_click_') !== -1) {
        isClickthrough = true;
        widgetId = Number(goalNameToCheck.substring(9, goalNameToCheck.length));
      } else if (goalNameToCheck.indexOf('oc_discount_') !== -1) {
        isDiscount = true;
        widgetId = Number(goalNameToCheck.substring(12, goalNameToCheck.length));
      } else if (goalNameToCheck.indexOf('oc_action_') !== -1) {
        isAction = true;
        // oc_action_{widgetId}_{stateId} for a state, plus _{componentId} for a component — the widget is
        // always the third segment, so it is read by index rather than by offset.
        widgetId = Number(goalNameToCheck.split('_')[2]);
      }
      goal.widgetId = widgetId;
      const campaignName = FlyoutHelpers.getCampaignNameByWidgetId(
        widgetId,
        widgetIdToCampaignIdMap,
        campaignIdToCampaignNameMap,
      );
      if (isClickthrough) {
        if (campaignName) {
          goal.name = languageService.translateInstantly('visits.goalEvents.clickThrough') + ': ' + campaignName;
        } else {
          goal.name =
            campaignName !== ''
              ? languageService.translateInstantly('visits.goalEvents.clickThroughRemoved', { widgetId: widgetId })
              : languageService.translateInstantly('visits.goalEvents.clickThrough') +
                ' ' +
                'in' +
                ' ' +
                `<strong>widget ${widgetId}</strong>`;
        }
      } else if (isConversion) {
        if (campaignName) {
          goal.name = languageService.translateInstantly('visits.goalEvents.formSubmission') + ': ' + campaignName;
        } else {
          goal.name =
            campaignName !== ''
              ? languageService.translateInstantly('visits.goalEvents.formSubmissionRemoved', { widgetId: widgetId })
              : languageService.translateInstantly('visits.goalEvents.formSubmission') +
                ' ' +
                'in' +
                ' ' +
                `<strong>widget ${widgetId}</strong>`;
        }
      } else if (isDiscount) {
        if (campaignName) {
          goal.name = languageService.translateInstantly('visits.goalEvents.discount') + ': ' + campaignName;
        } else {
          goal.name =
            campaignName !== ''
              ? languageService.translateInstantly('visits.goalEvents.discountRemoved', { widgetId: widgetId })
              : languageService.translateInstantly('visits.goalEvents.discount') +
                ' ' +
                'in' +
                ' ' +
                `<strong>widget ${widgetId}</strong>`;
        }
      } else if (isAction) {
        if (campaignName) {
          goal.name = languageService.translateInstantly('visits.goalEvents.componentAction') + ': ' + campaignName;
        } else {
          goal.name =
            campaignName !== ''
              ? languageService.translateInstantly('visits.goalEvents.componentActionRemoved', { widgetId: widgetId })
              : languageService.translateInstantly('visits.goalEvents.componentAction') +
                ' ' +
                'in' +
                ' ' +
                `<strong>widget ${widgetId}</strong>`;
        }
      } else {
        goal.name = goalNameToCheck;
      }
    }
    return goal;
  }

  private static populateActionListWithCorrectNames(
    actionList: Array<SessionAction>,
    widgetIdToCampaignIdMap: { number: number },
    campaignIdToCampaignNameMap: { number: string },
    languageService: LanguageService,
  ): Array<SessionAction> {
    if (!actionList) {
      return actionList;
    }
    actionList.forEach((action: SessionAction) => {
      if (
        action.type === SessionActionType.WidgetOpen ||
        action.type === SessionActionType.WidgetConversion ||
        action.type === SessionActionType.WidgetClickthrough ||
        action.type === SessionActionType.Event ||
        action.type === SessionActionType.PartialSubmitSurvey ||
        action.type === SessionActionType.PartialSubmit ||
        action.type === SessionActionType.WidgetDismissal
      ) {
        let widgetId: number;
        if (action.type === SessionActionType.Event) {
          if (action.eventId === null) {
            if (!action.eventId) {
              if (action.title === (ActionEventTitle.Partial || ActionEventTitle.Survey)) {
                widgetId = undefined;
              } else {
                widgetId = Number(action.title);
              }
              const campaignName = FlyoutHelpers.getCampaignNameByWidgetId(
                widgetId,
                widgetIdToCampaignIdMap,
                campaignIdToCampaignNameMap,
              );
              action.title =
                this.prettifyCampaignName(campaignName, widgetId, action.type, languageService) || action.title;
            }
          } else {
            widgetId = Number(action.eventId);
            const campaignName = FlyoutHelpers.getCampaignNameByWidgetId(
              widgetId,
              widgetIdToCampaignIdMap,
              campaignIdToCampaignNameMap,
            );
            action.title = this.prettifyCampaignName(campaignName, widgetId, action.type, languageService);
          }
        } else if (action.type === (SessionActionType.PartialSubmitSurvey || SessionActionType.PartialSubmit)) {
          widgetId = Number(action.title);
          const campaignName = FlyoutHelpers.getCampaignNameByWidgetId(
            widgetId,
            widgetIdToCampaignIdMap,
            campaignIdToCampaignNameMap,
          );
          action.title = this.prettifyCampaignName(campaignName, widgetId, action.type, languageService);
        } else {
          widgetId = Number(action.title);
          action.widgetId = widgetId;
          const campaignName = FlyoutHelpers.getCampaignNameByWidgetId(
            widgetId,
            widgetIdToCampaignIdMap,
            campaignIdToCampaignNameMap,
          );
          action.title = this.prettifyCampaignName(campaignName, widgetId, action.type, languageService);
        }
      } else if (action.type === SessionActionType.Goal) {
        const goalWithName = FlyoutHelpers.populateGoalWithConvertedName(
          { name: action.title.toLowerCase() },
          widgetIdToCampaignIdMap,
          campaignIdToCampaignNameMap,
          languageService,
        );
        action.title = goalWithName.name;
      } else if (action.type === SessionActionType.Scroll) {
        this.populateScrollEvents(action, actionList, languageService);
      }
    });
    return actionList;
  }

  private static populateScrollEvents(
    action: SessionAction,
    actionList: Array<SessionAction>,
    languageService: LanguageService,
  ) {
    const splitTitle = action.title.split('%');
    action.title = languageService.translateInstantly('flyout.actionList.scroll') + ': ' + splitTitle[0] + '%';
    const scrollEvents = actionList.filter((x) => x.type === SessionActionType.Scroll);
    const currentScrollIndex = scrollEvents.indexOf(action);
    if (currentScrollIndex !== -1 && currentScrollIndex !== 0) {
      const previousScrollValue = scrollEvents
        .find((_x, index) => index === currentScrollIndex - 1)
        ?.title.split('%')[0]
        .split(' ')[1];
      if (splitTitle[0] > previousScrollValue) {
        action.scrollDown = true;
      } else {
        action.scrollDown = false;
      }
    } else {
      action.scrollDown = true;
    }
  }

  private static prettifyCampaignName(
    campaignName: string,
    widgetId: number,
    actionType: SessionActionType,
    languageService: LanguageService,
  ): string {
    if (actionType === SessionActionType.WidgetOpen) {
      if (campaignName) {
        return languageService.translateInstantly('visits.goalEvents.displayedCampaign') + ': ' + campaignName;
      } else {
        return languageService.translateInstantly('visits.goalEvents.displayedWidgetRemoved', { widgetId: widgetId });
      }
    } else if (actionType === SessionActionType.WidgetClickthrough) {
      if (campaignName) {
        return languageService.translateInstantly('visits.goalEvents.clickThrough') + ': ' + campaignName;
      } else {
        return languageService.translateInstantly('visits.goalEvents.clickThroughRemoved', { widgetId: widgetId });
      }
    } else if (actionType === SessionActionType.WidgetConversion) {
      if (campaignName) {
        return languageService.translateInstantly('visits.goalEvents.formSubmission') + ': ' + campaignName;
      } else {
        return languageService.translateInstantly('visits.goalEvents.formSubmissionRemoved', { widgetId: widgetId });
      }
    } else if (actionType === SessionActionType.Event) {
      if (campaignName) {
        return languageService.translateInstantly('flyout.actionList.widget-conversion-partial') + ': ' + campaignName;
      } else if (campaignName === undefined) {
        return '';
      } else {
        return languageService.translateInstantly('visits.goalEvents.displayedWidgetRemoved', { widgetId: widgetId });
      }
    } else if (actionType === SessionActionType.PartialSubmitSurvey) {
      if (campaignName) {
        return languageService.translateInstantly('flyout.actionList.widget-conversion-partial') + ': ' + campaignName;
      } else if (campaignName === undefined) {
        return (
          languageService.translateInstantly('flyout.actionList.widget-conversion-partial') +
          ' ' +
          languageService.translateInstantly('flyout.actionList.unknown-campaign')
        );
      } else {
        return languageService.translateInstantly('visits.goalEvents.displayedWidgetRemoved', { widgetId: widgetId });
      }
    }
    return undefined;
  }

  private static getCampaignNameByWidgetId(
    widgetId: number,
    widgetIdToCampaignIdMap: { number: number },
    campaignIdToCampaignNameMap: { number: string },
  ): string {
    const campaignId = widgetIdToCampaignIdMap[widgetId];
    if (!campaignId) {
      return undefined;
    } else {
      return campaignIdToCampaignNameMap[campaignId];
    }
  }

  private static mergeOnsiteCampaignStats(
    calculatedDetails: CalculatedSessionDetails,
    widgetIdToCampaignIdMap: { number: number },
    campaignIdToCampaignNameMap: { number: string },
  ): void {
    const campaignStatsMap = {} as { number: OnsiteCampaignStatistics };

    if (calculatedDetails.widgetOpens) {
      calculatedDetails.widgetOpens.forEach((entry: CalculatedDataEntry) => {
        const campaignId = widgetIdToCampaignIdMap[Number(entry.name)];
        if (campaignStatsMap[campaignId]) {
          campaignStatsMap[campaignId].views = campaignStatsMap[campaignId].views + 1;
          if (campaignStatsMap[campaignId].widgetIds.indexOf(Number(entry.name)) === -1) {
            campaignStatsMap[campaignId].widgetIds.push(Number(entry.name));
          }
        } else {
          campaignStatsMap[campaignId] = {
            campaignId: campaignId,
            campaignName: campaignIdToCampaignNameMap[campaignId] ? campaignIdToCampaignNameMap[campaignId] : 'removed',
            views: 1,
            clicks: 0,
            conversions: 0,
            clickthroughs: 0,
            widgetIds: [Number(entry.name)],
          } as OnsiteCampaignStatistics;
        }
      });
    }

    if (calculatedDetails.widgetClickthroughs) {
      calculatedDetails.widgetClickthroughs.forEach((entry: CalculatedDataEntry) => {
        const campaignId = widgetIdToCampaignIdMap[Number(entry.name)];
        if (campaignStatsMap[campaignId]) {
          campaignStatsMap[campaignId].clickthroughs = campaignStatsMap[campaignId].clickthroughs + 1;
          if (campaignStatsMap[campaignId].widgetIds.indexOf(Number(entry.name)) === -1) {
            campaignStatsMap[campaignId].widgetIds.push(Number(entry.name));
          }
        } else {
          campaignStatsMap[campaignId] = {
            campaignId: campaignId,
            campaignName: campaignIdToCampaignNameMap[campaignId] ? campaignIdToCampaignNameMap[campaignId] : 'removed',
            views: 0,
            clicks: 0,
            conversions: 0,
            clickthroughs: 1,
            widgetIds: [Number(entry.name)],
          } as OnsiteCampaignStatistics;
        }
      });
    }

    if (calculatedDetails.widgetConversions) {
      calculatedDetails.widgetConversions.forEach((entry: CalculatedDataEntry) => {
        const campaignId = widgetIdToCampaignIdMap[Number(entry.name)];
        if (campaignStatsMap[campaignId]) {
          campaignStatsMap[campaignId].conversions = campaignStatsMap[campaignId].conversions + 1;
          if (campaignStatsMap[campaignId].widgetIds.indexOf(Number(entry.name)) === -1) {
            campaignStatsMap[campaignId].widgetIds.push(Number(entry.name));
          }
        } else {
          campaignStatsMap[campaignId] = {
            campaignId: campaignId,
            campaignName: campaignIdToCampaignNameMap[campaignId] ? campaignIdToCampaignNameMap[campaignId] : 'removed',
            views: 0,
            clicks: 0,
            conversions: 1,
            clickthroughs: 0,
            widgetIds: [Number(entry.name)],
          } as OnsiteCampaignStatistics;
        }
      });
    }

    calculatedDetails.onsiteCampaignStats = FlyoutHelpers.extractEntriesFromObject(campaignStatsMap);
  }

  public static parseCalculatedPropertiesOnPerson(personDetails: PersonDetails) {
    if (!personDetails) {
      return personDetails;
    }
    if (personDetails.goals) {
      personDetails.revenue = personDetails.goals.reduce((sales, goal) => sales + Number(goal.revenue), 0);
      personDetails.goals = this.filterGoalsToBeShown(personDetails.goals);
    }
    if (personDetails.visits) {
      personDetails.visits.forEach((visit: Visit) => {
        const timestamp = TimeUtils.getMoment(visit.time);
        visit.timestamp = timestamp.valueOf() / 1000;
        visit.formattedTimestamp = timestamp.format('YYYY-MM-DD HH:mm:ss');
      });
    }
    if (personDetails.consents) {
      personDetails.consents.forEach((consent: ConsentEvent) => {
        const timestamp = TimeUtils.getMoment(consent.time);
        consent.timestamp = timestamp.valueOf() / 1000;
        consent.formattedTimestamp = timestamp.format('YYYY-MM-DD HH:mm:ss');
        consent.formattedVersionDate = TimeUtils.getFormattedDate(consent.versionDate);
      });
    }
    return personDetails;
  }

  public static extractActionTypeFromActionList(
    actionsList: Array<SessionAction>,
    actionType: SessionActionType,
  ): Array<CalculatedDataEntry> {
    if (!actionsList) {
      return [];
    }
    const result = {} as { string: CalculatedDataEntry };
    actionsList.forEach((action: SessionAction) => {
      if (action.type === actionType) {
        if (result[action.title]) {
          result[action.title].timestamps.push(action.time);
          result[action.title].count = result[action.title].count + 1;
          if (action.url) {
            if (result[action.title].urls.indexOf(action.url.toLowerCase()) === -1) {
              result[action.title].urls.push(action.url.toLowerCase());
            }
          }
        } else {
          result[action.title] = {
            name: action.title,
            count: 1,
            timestamps: [action.time],
            urls: action.url ? [action.url] : [],
          };
        }
      }
    });
    return FlyoutHelpers.extractEntriesFromObject(result);
  }

  public static extractEntriesFromObject(object: any): Array<any> {
    if (!object) {
      return [];
    }
    const result = [];
    Object.keys(object).forEach((key) => {
      result.push(object[key]);
    });
    return result;
  }

  public static filterGoalsToBeShown(goals: Array<VisitGoal>): Array<VisitGoal> {
    const filteredGoals: VisitGoal[] = [];
    goals
      .filter(
        (x) =>
          !x.id.startsWith('oc_submit_') &&
          !x.id.startsWith('Gave consent') &&
          !x.id.startsWith('oc_click_') &&
          !x.id.startsWith('oc_discount_') &&
          !x.id.startsWith('oc_action_'),
      )
      .reverse()
      .forEach((currentGoal) => {
        const existedGoal = filteredGoals.find((goal) => goal.id === currentGoal.id);
        if (existedGoal) {
          const revenue = this.mergeRevenue(existedGoal, currentGoal).toString(); // Calculate revenue before eventual changes
          if (new Date(currentGoal.time) > new Date(existedGoal.time)) {
            Object.assign(existedGoal, currentGoal);
          }
          existedGoal.revenue = revenue; // Set revenue after eventual changes
        } else {
          filteredGoals.push(currentGoal);
        }
      });
    for (const goal of filteredGoals) {
      goal.count = goals.filter((g) => g.id === goal.id).length;
    }
    return filteredGoals;
  }

  private static mergeRevenue(existedGoal: VisitGoal, currentGoal: VisitGoal): number {
    return +(existedGoal.revenue ?? 0) + +(currentGoal.revenue ?? 0);
  }
}
